import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

export function CookiePolicy() {
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const [returnTo, setReturnTo] = useState(null);

  useEffect(() => {
    const checkoutReturnTo = localStorage.getItem('checkoutReturnTo');
    if (checkoutReturnTo) {
      setReturnTo(checkoutReturnTo);
    }
  }, []);

  const handleBackClick = () => {
    if (returnTo) {
      window.location.href = returnTo;
    } else {
      navigate(`/${language}/`);
    }
  };

  const backText = returnTo 
    ? (t('checkout.btnBack') || 'Back') + ' to checkout'
    : t('cookies.backToHome') || 'Back to home';

  return (
    <div className="min-h-screen bg-slate-950">
      <div className="max-w-4xl mx-auto px-6 py-16">
        <button 
          onClick={handleBackClick}
          className="text-primary hover:text-purple-400 transition-colors mb-8 inline-block cursor-pointer bg-transparent border-none"
        >
          ← {backText}
        </button>
        
        <h1 className="text-4xl font-heading font-bold text-white mb-8">
          {t('cookies.title')}
        </h1>

        <div className="prose prose-invert prose-slate max-w-none">
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-white mb-4">
              {t('cookies.whatAreCookiesTitle')}
            </h2>
            <p className="text-slate-300 leading-relaxed">
              {t('cookies.whatAreCookiesText')}
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-white mb-4">
              {t('cookies.essentialCookiesTitle')}
            </h2>
            <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-6">
              <p className="text-slate-300 leading-relaxed">
                {t('cookies.essentialCookiesText')}
              </p>
              <p className="text-slate-400 mt-3">
                {t('cookies.essentialCookiesNote')}
              </p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-white mb-4">
              {t('cookies.analyticsCookiesTitle')}
            </h2>
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-6">
              <p className="text-slate-300 leading-relaxed mb-3">
                {t('cookies.analyticsCookiesText')}
              </p>
              <p className="text-slate-400">
                {t('cookies.analyticsCookiesNote')}
              </p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-white mb-4">
              {t('cookies.managingCookiesTitle')}
            </h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              {t('cookies.managingCookiesText')}
            </p>
            <ul className="space-y-2 text-slate-300">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>{t('cookies.chromeSettings')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>{t('cookies.firefoxSettings')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>{t('cookies.safariSettings')}</span>
              </li>
            </ul>
            <p className="text-slate-400 mt-4">
              {t('cookies.disablingNote')}
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-white mb-4">
              {t('cookies.thirdPartyCookiesTitle')}
            </h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              {t('cookies.thirdPartyCookiesText')}
            </p>
            <ul className="space-y-2 text-slate-300">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>
                  <span className="font-semibold text-white">Stripe</span> – {t('cookies.stripeLabel').split('– ')[1]}
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>
                  <span className="font-semibold text-white">{t('cookies.analyticsLabel').split('– ')[0]}</span> – {t('cookies.analyticsLabel').split('– ')[1]}
                </span>
              </li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-white mb-4">
              {t('cookies.updatesTitle')}
            </h2>
            <p className="text-slate-300 leading-relaxed">
              {t('cookies.updatesText')}
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-white mb-4">
              {t('cookies.contactTitle')}
            </h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              {t('cookies.contactText')}
            </p>
            <a href="mailto:info@dathent.com" className="text-primary hover:text-purple-400 transition-colors text-lg">
              info@dathent.com
            </a>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-slate-800">
          <p className="text-sm text-slate-500">
            {t('cookies.lastUpdated')}
          </p>
        </div>
      </div>
    </div>
  );
}
